import React, { useEffect, useState } from 'react'
import styled, { withTheme } from 'styled-components'
import { inject, observer } from 'mobx-react'
import { Link } from 'react-router-dom'
import { Trans, withTranslation } from 'react-i18next'
import { Alert, Form, Spinner } from 'react-bootstrap'

import { colors } from '../../styles/colors'
import {
  CategoryIconDefault,
  CategoryIcons,
} from '../Icons/CategoryIcons/CategoryIcons'
import SafelySetInnerHTML from '../SafelySetInnerHTML'
import { AddToBasketButton } from '../Buttons/AddToBasketButton'
import useMountEffect from '../../utils/hooks/useMountEffect'
import { useNavigate } from 'react-router-dom'
import MediaQuery from 'react-responsive'
import FavoriteItem from '../Favorite/FavoriteItem'

const StyledMainWrapper = styled.div`
  color: ${colors.gray4};
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  margin-bottom: 20px;

  &:hover {
    color: ${colors.gray4};
  }
`

const StyledProductCardWrapper = styled.div`
  border-radius: 4px;
  box-shadow: 0 0 15px rgba(0, 0, 0, 0.2);
  height: 230px;
  overflow: hidden;
  position: relative;
  width: 100%;
`

const StyledProductCardImgWrapper = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;

  &.placeholder-img {
    svg {
      width: 100%;
      height: 100%;
      opacity: 0.3;
    }
  }
`

const StyledProductCardImg = styled.img`
  max-width: 185px;
  max-height: 150px;
`

const StyledMaxWrapper = styled.div`
  position: absolute;
  right: 0;
  bottom: 0;
  background-color: #fff;
  border-top-left-radius: 4px;
  padding: 8px 12px;
  font-weight: 600;
`

const StyledMaxLabel = styled.div`
  font-size: 11px;
  color: ${colors.gray3};
`

const StyledMaxCounter = styled.div`
  font-size: 14px;
  color: ${colors.gray4};
`

const StyledDescription = styled.div`
  padding: 12px 12px 0px 12px;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  .is-inactive & {
    opacity: 0.3;
  }

  .btn-primary svg {
    margin-right: 0 !important;
  }
`

const StyledArticleNumberSpan = styled.span`
  font-size: 12px;
  color: ${colors.gray3};
  text-transform: uppercase;
`
const StyledArticleInfoSpan = styled.span`
  font-size: 12px;
  color: ${colors.gray3};
`

const StyledArticleCompare = styled.div`
  padding: 1px 1px;
  margin: 0;
  vertical-align: center;
  border-bottom: 1px solid ${colors.gray3};
  font-size: small;

  label {
    margin: 0px;
    padding-left: 5px;
  }
`

const StyledPrice = styled.div`
  color: ${(props) => props.theme.primaryColor};
  font-weight: bold;
`

const StyledCardBadge = styled.div`
  background-color: ${(props) => props.theme.primaryColor};
  color: #fff;
  position: absolute;
  bottom: 0;
  left: 0;
  border-top-right-radius: 4px;
  padding: 4px 6px;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
`

const StyledInactiveLayer = styled.div`
  position: absolute;
  z-index: 1;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  text-transform: uppercase;
  text-align: center;

  &::before {
    content: '';
    position: absolute;
    z-index: -1;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: #fff;
    opacity: 0.7;
  }
`

const StyledInactiveText = styled.div`
  position: relative;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`

const StyledIMGDescription = styled.div`
  background-color: ${(props) => props.theme.primaryColor};
  color: #fff;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  font-size: 13px;
  letter-spacing: 0.4px;
  text-align: center;
`

const StyledAlert = styled(Alert)`
  position: fixed;
  top: 55px;
  right: 20px;
  z-index: 1050;
  width: auto;
  max-width: 300px;
  transition: opacity 0.5s ease-out, transform 0.2 ease-out,
    visibility 0.2s ease-out;
  transform: translateY(0);
  opacity: 1;

  &.hidden {
    opacity: 0;
    transform: translateY(-20px);
  }
`

const ProductCard = inject(
  'teamOrderStore',
  'productsCompareStore',
  'uiStore',
  'productDetailsStore',
  'productListStore',
  'productsStore'
)(
  observer((props) => {
    const {
      t,
      productsCompareStore,
      teamOrderStore,
      uiStore,
      theme,
      productDetailsStore,
      productListStore,
      shoppingBasketArticle,
      productsStore,
      catalogOnly,
    } = props
    const CategoryIcon = CategoryIcons[props.iconType]
      ? CategoryIcons[props.iconType]
      : CategoryIconDefault
    const shoppingBasketArticleId = shoppingBasketArticle.id
    const currentProductObj =
      productListStore.productObj[shoppingBasketArticleId]
    const [visibleSuccess, setVisibleSuccess] = useState(false)
    const [visibleErrors, setVisibleErrors] = useState(false)
    const [invalidQuantity, setInvalidQuantity] = useState(false)
    const navigate = useNavigate()

    useMountEffect(() => {
      if (!currentProductObj) {
        ;(async function () {
          productListStore.initProductObject(shoppingBasketArticle)
          await productListStore.loadOrderableVariants(shoppingBasketArticleId)
          productListStore.loadAllVariantsQuantities(shoppingBasketArticleId)
          productListStore.loadArticlePrices(shoppingBasketArticleId)
          productListStore.loadArticleConfiguration(shoppingBasketArticleId)
          await productListStore.loadLocalStorehouses(shoppingBasketArticleId)
        })()
      }
    })

    useEffect(() => {
      if (currentProductObj?.addToBasketSuccess) {
        setVisibleSuccess(true)
        const timer = setTimeout(() => setVisibleSuccess(false), 4000)
        return () => clearTimeout(timer)
      }
    }, [currentProductObj?.addToBasketSuccess])
    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
      if (currentProductObj?.addToBasketErrors.length) {
        setVisibleErrors(currentProductObj.addToBasketErrors)

        const timer = setTimeout(() => {
          setVisibleErrors([])
        }, 4000)

        return () => clearTimeout(timer)
      }
    }, [JSON.stringify(currentProductObj?.addToBasketErrors)])
    /* eslint-enable react-hooks/exhaustive-deps */
    useEffect(() => {
      if (productDetailsStore.scrollRef === props.id) {
        let element = document.querySelector(`[data-id="${props.id}"]`)
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
        productDetailsStore.setScrollRef(null)
      }
    }, [productDetailsStore, props.id])

    useEffect(() => {
      // Initialize the product object for this article if it hasn't been initialized already.
      if (!productListStore.productObj[shoppingBasketArticle.id]) {
        productListStore.initProductObject(shoppingBasketArticle)
      }
    }, [productListStore, shoppingBasketArticle])

    const handleChangeQuantity = (e, shoppingBasketArticleId) => {
      productListStore.handleChangeQuantity(e, shoppingBasketArticleId)
      const remainingQuantity =
        currentProductObj.maximumQuantity -
        currentProductObj.articleAvailability.amountToOrder
      if (
        currentProductObj.maximumQuantity !== null &&
        e.target.value > remainingQuantity
      ) {
        setInvalidQuantity(true)
      } else {
        setInvalidQuantity(false)
      }
    }

    return (
      <StyledMainWrapper data-id={props.id}>
        <Link
          to={props.to ? props.to : '#'}
          className={`${props.className} ${
            props.isInactive ? 'is-inactive' : ''
          } `}
          onClick={props.onClick}
        >
          <StyledProductCardWrapper className='no-border-radius'>
            {props.badge && <StyledCardBadge>{props.badge}</StyledCardBadge>}

            {props.image && props.image.description && (
              <StyledIMGDescription>
                <SafelySetInnerHTML className='d-flex justify-content-center'>
                  {props.image.description}
                </SafelySetInnerHTML>
              </StyledIMGDescription>
            )}

            <StyledProductCardImgWrapper
              className={!props.image ? 'placeholder-img' : ''}
            >
              {props.image && props.image.url ? (
                <StyledProductCardImg src={props.image.url} />
              ) : (
                <CategoryIcon
                  color={theme.primaryColor}
                  className={props.iconType}
                />
              )}
              {props.isInactive ? (
                <StyledInactiveLayer>
                  <StyledInactiveText className='text-danger'>
                    {typeof props.isInactive === 'object' ? (
                      props.isInactive
                    ) : (
                      <Trans i18nKey='productsOverview:maxAmountReached' />
                    )}
                  </StyledInactiveText>
                </StyledInactiveLayer>
              ) : (
                ''
              )}
            </StyledProductCardImgWrapper>

            <StyledMaxWrapper className='d-flex flex-column align-items-end'>
              {props.max && (
                <>
                  <StyledMaxLabel className='text-uppercase'>
                    {t('maxAmount')}
                  </StyledMaxLabel>
                  <StyledMaxCounter
                    className={props.isAmountMaxedOut ? 'text-danger' : ''}
                  >
                    {props.max}
                  </StyledMaxCounter>
                </>
              )}
              {props.current || props.amountToOrder || props.current === 0 ? (
                <>
                  <StyledMaxLabel className='text-uppercase'>
                    {t('currentAmount')}
                  </StyledMaxLabel>
                  <StyledMaxCounter
                    className={props.isAmountMaxedOut ? 'text-danger' : ''}
                  >
                    {props.current || props.current === 0 ? props.current : ''}
                    <span className='text-primary'>
                      {props.amountToOrder ? `(+${props.amountToOrder})` : ''}
                    </span>
                  </StyledMaxCounter>
                </>
              ) : (
                ''
              )}
            </StyledMaxWrapper>
          </StyledProductCardWrapper>
        </Link>
        <StyledDescription>
          <div>{props.name}</div>
          {uiStore.showArticleNumberInOverview && (
            <StyledArticleNumberSpan>
              {t('productDetail:articleNumber')} {props.articleNumber}
            </StyledArticleNumberSpan>
          )}
          <StyledArticleInfoSpan>
            {shoppingBasketArticle.article.aliases?.map((alias) => (
              <div key={alias.id}>
                {alias.aliasType}: {alias.displayName || '-'}
              </div>
            ))}
          </StyledArticleInfoSpan>
          {(shoppingBasketArticle?.article?.unit?.displayName ??
            shoppingBasketArticle?.article?.unit?.isoCode) && (
            <StyledArticleInfoSpan
              style={{ marginBottom: '5px', display: 'inline-block' }}
            >
              {t('unit')}:&nbsp;
              {shoppingBasketArticle?.article?.unit?.displayName ??
                shoppingBasketArticle?.article?.unit?.isoCode}
            </StyledArticleInfoSpan>
          )}
          <small>{props.subDescription}</small>
          {currentProductObj ? (
            !productsStore.initialClothing ? (
              <div>
                <div className='d-flex justify-content-between align-items-end'>
                  {/* Conditionally render AddToBasketButton and quantity control if no sizes */}

                  {catalogOnly ? null 
                  : (currentProductObj &&
                    currentProductObj.variants &&
                    !currentProductObj.variants.length > 0) && (
                      <>
                        <AddToBasketButton
                          shoppingBasketArticle={shoppingBasketArticle}
                          onClick={() => {
                            if (
                              currentProductObj &&
                              currentProductObj.variants &&
                              currentProductObj.variants.length > 0
                            ) {
                              if (
                                productListStore.productObj[
                                  shoppingBasketArticle.id
                                ]
                              ) {
                                navigate(
                                  productDetailsStore.getProductToUrl(
                                    shoppingBasketArticle
                                  )
                                )
                              } else {
                                console.error(
                                  `Product object for ID ${shoppingBasketArticle.id} is not initialized.`
                                )
                              }
                            } else {
                              productListStore.handleAddToBasket(
                                shoppingBasketArticle.id
                              )
                            }
                          }}
                          variant='primary'
                          loadingText={t('productDetail:buttonWaitText')}
                          isLoading={currentProductObj?.addToBasketIsLoading}
                        />

                        <Form.Control
                          className='mx-2'
                          type='number'
                          placeholder='1'
                          value={currentProductObj.selectedQuantity || ''}
                          onChange={(e) =>
                            handleChangeQuantity(e, shoppingBasketArticleId)
                          }
                          min={currentProductObj.minimumQuantity}
                          max={currentProductObj.maximumQuantity}
                          step={currentProductObj.packageSize}
                          isInvalid={invalidQuantity}
                        />
                      </>
                    )}

                  {!props.shouldHidePricesAndPoints && (
                    <StyledPrice>{props.price}</StyledPrice>
                  )}
                </div>
                <MediaQuery maxWidth={507}>
                  {currentProductObj &&
                  currentProductObj.addToBasketSuccess &&
                  currentProductObj.variants &&
                  currentProductObj.variants.length === 0 ? (
                    <Alert variant='success' className='w-100 mt-2'>
                      {t('productDetail:productAddedSuccesfullyToBasket')}
                    </Alert>
                  ) : null}

                  {visibleErrors &&
                  currentProductObj &&
                  currentProductObj.addToBasketErrors.length &&
                  currentProductObj.variants &&
                  currentProductObj.variants.length === 0
                    ? currentProductObj.addToBasketErrors.map(
                        (error, errorIndex) => (
                          <Alert
                            key={errorIndex}
                            variant='danger'
                            className='w-100 mt-2'
                          >
                            {error.msg}
                          </Alert>
                        )
                      )
                    : ''}
                </MediaQuery>
                <MediaQuery minWidth={508}>
                  {currentProductObj &&
                  currentProductObj.addToBasketSuccess &&
                  visibleSuccess ? (
                    <StyledAlert
                      variant='success'
                      className={visibleSuccess ? '' : 'hidden'}
                    >
                      {t('productDetail:productAddedSuccesfullyToBasket')}
                    </StyledAlert>
                  ) : (
                    ''
                  )}
                  {visibleErrors &&
                  currentProductObj &&
                  currentProductObj.addToBasketErrors.length
                    ? currentProductObj.addToBasketErrors.map(
                        (error, errorIndex) => (
                          <StyledAlert key={errorIndex} variant='danger'>
                            {error.msg}
                          </StyledAlert>
                        )
                      )
                    : ''}
                </MediaQuery>
              </div>
            ) : (
              <div>
                {!props.shouldHidePricesAndPoints && (
                  <StyledPrice>{props.price}</StyledPrice>
                )}
                <Link
                  className='btn btn-outline-secondary mb-2 mt-1 w-100'
                  style={{ padding: '6px 12px' }}
                  to={productDetailsStore.getProductToUrl(
                    shoppingBasketArticle
                  )}
                  onClick={(e) => {
                    sessionStorage.setItem(
                      'scrollPosition',
                      window.scrollY.toString()
                    )
                    sessionStorage.setItem(
                      'lastViewedProduct',
                      shoppingBasketArticle.article.id.toString()
                    )

                    const articleWithDefaults = {
                      ...shoppingBasketArticle,
                      articleAvailability:
                        shoppingBasketArticle.articleAvailability || {},
                      categoryAvailability:
                        shoppingBasketArticle.categoryAvailability || {},
                    }

                    productDetailsStore.setShoppingBasketArticle(
                      articleWithDefaults
                    )
                  }}
                >
                  {t('inventory:goToArticle')}
                </Link>
              </div>
            )
          ) : (
            <Spinner animation='border' variant='primary' size='sm' />
          )}
        </StyledDescription>
        {theme.enableCostCenterProductsComparison &&
        teamOrderStore.selectedCostcenterId ? (
          <StyledArticleCompare>
            <div>
              <Form.Check
                name='articleCompare'
                id={props.id}
                checked={productsCompareStore.articles.some(
                  (art) => art.id === props.id
                )}
                disabled={
                  productsCompareStore.articles.length >= 5 &&
                  !productsCompareStore.articles.some(
                    (art) => art.id === props.id
                  )
                }
                onChange={() =>
                  productsCompareStore.onArticleCompareHandler(
                    props.id,
                    props.to,
                    props.price
                  )
                }
                label={t('articleComparison:compareArticle')}
              />
            </div>
          </StyledArticleCompare>
        ) : null}
        {uiStore.allowFavoriteArticleCatalogForOrdering && (
          <FavoriteItem articleId={shoppingBasketArticle.article.id} />
        )}
      </StyledMainWrapper>
    )
  })
)

export default withTheme(withTranslation()(ProductCard))
